import { Settings } from 'lucide-react';
import { createSearchParams, useNavigate } from 'react-router';
import { Fragment } from 'react';

import { SummaryCard } from 'src/containers/Escrow/Summary';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  activationNotificationOptionsRichDictionary,
  assetVisibilityOptionsRichDictionary,
} from 'src/containers/SoftwareEscrow/Technical';
import { Table, TableBody, TableCell, TableRow, TableFooter } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { storageRegionDictionary } from 'src/shared/entities';
import { AgreementFormEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';
import { cn } from 'src/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  gracePeriodOptionsDictionary,
  disruptedNotificationsOptionsRichDictionary,
} from 'src/shared/entities/escrow-notification-options';
import { getEscrowActivityNotificationOptions } from 'src/shared/entities/escrow-activity-notification-options';
import { assetVersionsLimitOptionsDictionary } from 'src/shared/entities/asset-versions-limit-options';

const { activityNotificationOptionsRichDictionary } = getEscrowActivityNotificationOptions('software');

const PartyNotifications = ({ onEdit, title }: { onEdit: (slug: string) => void; title?: string }) => {
  const {
    softwareEscrow: {
      currentCompanyRole,
      [`${currentCompanyRole!}ActivityNotification` as const]: activityNotification,
      [`${currentCompanyRole!}DisruptionNotification` as const]: disruptionNotification,
      [`${currentCompanyRole!}DisruptionNotificationGracePeriod` as const]: disruptionNotificationGracePeriod,
    },
  } = useSoftwareEscrow();
  const {
    icon: ActivityIcon,
    label: activityTitle,
    description: activityDescription,
  } = activityNotificationOptionsRichDictionary[activityNotification];
  const { icon: DisruptionIcon, label: disruptionTitle } =
    disruptedNotificationsOptionsRichDictionary[disruptionNotification];
  const gracePeriod = gracePeriodOptionsDictionary[disruptionNotificationGracePeriod ?? '']?.label;

  const handleEdit = () => onEdit('notification-settings');

  return (
    <div className="space-y-2">
      {title && <div className="text-sm font-bold text-foreground">{title} settings</div>}
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableBody>
            <TableRow className="border-0 hover:bg-white">
              <TableCell className="w-47.5 font-semibold text-xs text-muted-foreground">
                Activity notifications
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2 min-w-[250px]">
                  <div className="size-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                    <ActivityIcon className="size-4 text-foreground" />
                  </div>
                  <div className="space-y-1">
                    <div className="font-semibold text-sm">{activityTitle}</div>
                    <div className="text-xs text-muted-foreground">{activityDescription}</div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
            <TableRow className="hover:bg-white">
              <TableCell className="w-47.5 font-semibold text-xs text-muted-foreground">
                Disrupted access notifications
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div className="size-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                    <DisruptionIcon className="size-4 text-foreground" />
                  </div>
                  <div className="font-semibold text-sm">
                    {disruptionTitle}
                    {gracePeriod && ` (${gracePeriod})`}
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} className="text-right">
                <Button variant="secondary" size="sm" onClick={handleEdit}>
                  Edit
                </Button>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

const StorageSettings = ({ onEdit }: { onEdit: (slug: string) => void }) => {
  const {
    softwareEscrow: { storageRegion, assetVersionsLimit },
  } = useSoftwareEscrow();

  const handleEdit = () => onEdit('storage-settings');

  const hasAddOn = storageRegion !== 'default' || !assetVersionsLimit;

  return (
    <div className="space-y-2">
      <div className="text-sm font-bold text-foreground">Storage</div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableBody>
            <TableRow className="border-0 hover:bg-white">
              <TableCell className="w-47.5 font-semibold text-xs text-muted-foreground">Storage region</TableCell>
              <TableCell className={cn(hasAddOn && 'w-47.5')}>{storageRegionDictionary[storageRegion || '']}</TableCell>
              {storageRegion !== 'default' && (
                <TableCell>
                  <Badge>Add-on</Badge>
                </TableCell>
              )}
            </TableRow>
            <TableRow className="hover:bg-white">
              <TableCell className="w-47.5 font-semibold text-xs text-muted-foreground">Version history</TableCell>
              <TableCell className={cn(hasAddOn && 'w-47.5')}>
                {assetVersionsLimitOptionsDictionary[assetVersionsLimit ?? 'full']}
              </TableCell>
              {!assetVersionsLimit && (
                <TableCell>
                  <Badge>Add-on</Badge>
                </TableCell>
              )}
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={hasAddOn ? 4 : 3} className="text-right">
                <Button variant="secondary" size="sm" onClick={handleEdit}>
                  Edit
                </Button>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

const InvitedPartyNotificationsCard = ({ onEdit }: { onEdit: (slug: string) => void }) => {
  const {
    softwareEscrow: { beneficiarySharedInformation },
  } = useSoftwareEscrow();

  const { icon: ActivityIcon, label: activityTitle } = activityNotificationOptionsRichDictionary.first;

  return (
    <SummaryCard
      icon={Settings}
      title="Technical settings"
      description="Notification settings"
      note={!beneficiarySharedInformation && 'Non-editable'}
    >
      <div className="space-y-5">
        {!beneficiarySharedInformation ? (
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableBody>
                <TableRow className="hover:bg-white">
                  <TableCell className="w-47.5 font-semibold text-xs text-muted-foreground">
                    Activity notification
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 min-w-[250px]">
                      <div className="size-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                        <ActivityIcon className="size-4 text-slate-700" />
                      </div>
                      <div className="space-y-1 grow">
                        <div className="font-medium text-sm">{activityTitle}</div>
                        <div className="text-xs text-muted-foreground">
                          Members will only receive an activation notification email once the escrow is activated.
                        </div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        ) : (
          <PartyNotifications onEdit={onEdit} />
        )}
      </div>
    </SummaryCard>
  );
};

const DepositorSettings = ({ onEdit }: { onEdit: (slug: string) => void }) => {
  const {
    softwareEscrow: {
      currentCompanyRole,
      depositorActivityNotification: activityNotification,
      depositorDisruptionNotification: disruptionNotification,
      depositorDisruptionNotificationGracePeriod: disruptionNotificationGracePeriod,
    },
  } = useSoftwareEscrow();
  const {
    icon: ActivityIcon,
    label: activityTitle,
    description: activityDescription,
  } = activityNotificationOptionsRichDictionary[activityNotification];
  const { icon: DisruptionIcon, label: disruptionTitle } =
    disruptedNotificationsOptionsRichDictionary[disruptionNotification];
  const gracePeriod = gracePeriodOptionsDictionary[disruptionNotificationGracePeriod ?? '']?.label;

  const handleEdit = () => onEdit('notification-settings');

  if (currentCompanyRole === EscrowPartyTypeEnum.Beneficiary) return null;

  return (
    <div className="space-y-2">
      <div className="text-sm font-bold text-foreground">Depositor settings</div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableBody>
            <TableRow className="border-0 hover:bg-white">
              <TableCell className="w-47.5 font-semibold text-xs text-muted-foreground">
                Activity notifications
              </TableCell>
              <TableCell>
                <div className="flex items-start gap-3">
                  <div className="size-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                    <ActivityIcon className="size-4 text-slate-700" />
                  </div>
                  <div className="space-y-1 grow">
                    <div className="font-semibold text-sm">{activityTitle}</div>
                    <div className="text-xs text-muted-foreground">{activityDescription}</div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
            <TableRow className="hover:bg-white">
              <TableCell className="w-47.5 font-semibold text-xs text-muted-foreground">
                Disrupted access notifications
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div className="size-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                    <DisruptionIcon className="size-4 text-slate-700" />
                  </div>
                  <div className="font-semibold text-sm">
                    {disruptionTitle}
                    {gracePeriod && ` (${gracePeriod})`}
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} className="text-right">
                <Button variant="secondary" size="sm" onClick={handleEdit}>
                  Edit
                </Button>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

const BeneficiarySettingsTripartite = ({ onEdit }: { onEdit: (slug: string) => void }) => {
  const {
    softwareEscrow: {
      currentCompanyRole,
      beneficiarySharedInformation,
      beneficiaryActivityNotification: activityNotification,
      beneficiaryDisruptionNotification: disruptionNotification,
      beneficiaryDisruptionNotificationGracePeriod: disruptionNotificationGracePeriod,
    },
  } = useSoftwareEscrow();
  const {
    icon: ActivityIcon,
    label: activityTitle,
    description: activityDescription,
  } = activityNotificationOptionsRichDictionary[activityNotification];
  const { icon: DisruptionIcon, label: disruptionTitle } =
    disruptedNotificationsOptionsRichDictionary[disruptionNotification];
  const gracePeriod = gracePeriodOptionsDictionary[disruptionNotificationGracePeriod ?? '']?.label;
  const {
    icon: AssetVisibilityIcon,
    label: assetVisibilityTitle,
    description: assetVisibilityDescription,
  } = assetVisibilityOptionsRichDictionary[String(beneficiarySharedInformation)];

  const handleEdit = () => onEdit('asset-visibility');

  return (
    <div className="space-y-2">
      <div className="text-sm font-bold text-foreground">Beneficiary settings</div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableBody>
            <TableRow className="border-0 hover:bg-white">
              <TableCell className="w-47.5 font-semibold text-xs text-muted-foreground">Asset visibility</TableCell>
              <TableCell>
                <div className="flex items-center gap-2 min-w-[250px]">
                  <div className="size-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                    <AssetVisibilityIcon className="size-4 text-slate-700" />
                  </div>
                  <div className="space-y-1 grow">
                    <div className="font-semibold text-sm">{assetVisibilityTitle}</div>
                    <div className="text-xs text-muted-foreground">{assetVisibilityDescription}</div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
            {currentCompanyRole === EscrowPartyTypeEnum.Beneficiary && beneficiarySharedInformation && (
              <Fragment>
                <TableRow className="border-0 hover:bg-white">
                  <TableCell className="w-47.5 font-semibold text-xs text-muted-foreground">
                    Activity notifications
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 min-w-[250px]">
                      <div className="size-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                        <ActivityIcon className="size-4 text-foreground" />
                      </div>
                      <div className="space-y-1">
                        <div className="font-medium text-sm">{activityTitle}</div>
                        <div className="text-xs text-muted-foreground">{activityDescription}</div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow className="hover:bg-white">
                  <TableCell className="w-47.5 font-semibold text-xs text-muted-foreground">
                    Disrupted access notifications
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="size-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                        <DisruptionIcon className="size-4 text-foreground" />
                      </div>
                      <div className="font-semibold text-sm">
                        {disruptionTitle}
                        {gracePeriod && ` (${gracePeriod})`}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </Fragment>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3}>
                <div className="flex justify-end">
                  <Button variant="secondary" size="sm" onClick={handleEdit}>
                    Edit
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

const BeneficiarySettingsBipartite = ({ onEdit }: { onEdit: (slug: string) => void }) => {
  const {
    softwareEscrow: { activationNotification },
  } = useSoftwareEscrow();

  const {
    icon: Icon,
    label,
    description,
  } = activationNotificationOptionsRichDictionary[String(activationNotification)];

  const handleEdit = () => onEdit('activation-notifications');

  return (
    <div className="space-y-2">
      <div className="text-sm font-bold text-foreground">Beneficiary settings</div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableBody>
            <TableRow className="hover:bg-white">
              <TableCell className="w-47.5 font-semibold text-xs text-muted-foreground">
                Activation notification
              </TableCell>
              <TableCell>
                <div className="flex gap-2 min-w-62.5">
                  <div className="size-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                    <Icon className="size-4 text-foreground" />
                  </div>
                  <div className="space-y-1 grow">
                    <div className="font-semibold text-sm">{label}</div>
                    <div className="text-xs text-muted-foreground">{description}</div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3}>
                <div className="flex justify-end">
                  <Button variant="secondary" size="sm" onClick={handleEdit}>
                    Edit
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

const TechnicalSettingsCard = () => {
  const navigate = useNavigate();
  const {
    softwareEscrow: {
      id,
      currentCompanyRole,
      startedBy,
      agreement: { agreementForm },
    },
  } = useSoftwareEscrow();

  const onEdit = (slug: string) =>
    navigate({
      pathname: `/software-escrows/onboarding/${id}/${slug}`,
      search: createSearchParams({
        isEdit: 'true',
      }).toString(),
    });

  const isInvitedParty = currentCompanyRole !== startedBy;

  if (isInvitedParty) return <InvitedPartyNotificationsCard onEdit={onEdit} />;

  return (
    <SummaryCard
      icon={Settings}
      title="Technical settings"
      description="The types of notifications and information escrow members will receive, and the storage for escrowed material."
    >
      <div className="space-y-5">
        <DepositorSettings onEdit={onEdit} />
        {agreementForm === AgreementFormEnum.Tripartite ? (
          <BeneficiarySettingsTripartite onEdit={onEdit} />
        ) : (
          <BeneficiarySettingsBipartite onEdit={onEdit} />
        )}
        <StorageSettings onEdit={onEdit} />
      </div>
    </SummaryCard>
  );
};

export default TechnicalSettingsCard;
